// De categorie van een maatregel in indicator 8 van de SiSa/SPUK-verantwoording
// energiearmoede (T21 van docs/features/huishouden-vaste-gegevens.md):
//
//   1 kleine gasbesparende maatregelen (w.o. radiatorfolie en tochtstrips)
//   2 kleine elektriciteitbesparende maatregelen (w.o. ledlampen)
//   3 grote(re) gasbesparende maatregelen (w.o. dak-, vloer-, spouwmuurisolatie)
//   4 grote(re) elektriciteitbesparende maatregelen (w.o. koelkast, wasmachine vervangen)
//   5 gegeven adviezen met directe verlaging van verbruik of rekening
//   6 overige energierekening verlagende maatregelen
//
// Een EXPLICIETE tabel per interventiecode, geen afleiding uit de bibliotheek
// bij het lezen: 'klein' of 'groot' staat nergens in de standaard, en een
// verantwoording die verschuift zodra de bibliotheek een kengetal bijwerkt is
// niet te controleren. De indeling volgt de milieu-circulariteitstandaard 0.11
// (`categorie` + `kengetallen`, fixture
// src/helpers/meetstandaard/__fixtures__/milieu-circulariteit-0.11.trimmed.json):
// gas = kengetal gasM3PerJaar > 0 (ook de waterbesparers: die sparen warm water),
// elektra = elektraKwhPerJaar > 0. Groot = een investering of vervanging
// (isolatie, voorzetraam, apparaat vervangen). Wat geen gas of stroom bespaart,
// of wat de standaard niet kwantificeert zonder richting, is 'overig'.
// Een nieuwe code in de standaard valt onder 'overig' tot hij hier staat.
//
// Advies volgt uit het SOORT materiaal (`Kind: 'advice'`), niet uit de code.
// AI-CONTEXT: See docs/features/huishouden-vaste-gegevens.md (T21)

import { isAdvice } from './materialCatalog'
import { defaultForMaterial } from './materialInterventionDefaults'

/** @typedef {'klein-gas'|'klein-elektra'|'groot-gas'|'groot-elektra'|'advies'|'overig'} SisaMeasureCategory */

/** @type {Array<{key: SisaMeasureCategory, label: string}>} */
const SISA_MEASURE_CATEGORIES = [
    { key: 'klein-gas', label: 'Kleine gasbesparende maatregelen' },
    { key: 'klein-elektra', label: 'Kleine elektriciteitbesparende maatregelen' },
    { key: 'groot-gas', label: 'Grote(re) gasbesparende maatregelen' },
    { key: 'groot-elektra', label: 'Grote(re) elektriciteitbesparende maatregelen' },
    { key: 'advies', label: 'Gegeven adviezen' },
    { key: 'overig', label: 'Overige maatregelen' },
]

/** @type {Record<string, SisaMeasureCategory>} */
const BY_CODE = {
    // Klein gas — Verwarming en Water met gaskengetal.
    ET01: 'klein-gas', // radiatorfolie
    ET03: 'klein-gas', // kierdichting
    ET03a: 'klein-gas', // tochtband
    ET04: 'klein-gas', // isolerende gordijnen
    ET07: 'klein-gas', // waterbesparende douchekop (warm water)
    ET09: 'klein-gas', // douchetimer (warm water)
    ET10: 'klein-gas', // radiatorventilator
    ET12: 'klein-gas', // waterzijdig inregelen cv
    ET14: 'klein-gas', // perlator (warm water)
    ET15: 'klein-gas', // kozijnfolie
    ET15b: 'klein-gas', // glasfolie
    ET16: 'klein-gas', // brievenbusborstel
    ET18: 'klein-gas', // vensterbankrooster
    ET19: 'klein-gas', // deursluiter (zonering; categorie Verwarming, nog zonder kengetal)
    ET24: 'klein-gas', // deuren beter sluitend
    ET25: 'klein-gas', // ramen beter sluitend
    LK01: 'klein-gas', // ventilatieroosters reinigen (categorie Verwarming)
    // Klein elektra.
    ET02: 'klein-elektra', // ledlamp
    ET17: 'klein-elektra', // tijdschakelaar
    ET20: 'klein-elektra', // bewegingssensor (categorie Elektra, nog zonder kengetal)
    // Groot gas — investering in schil of verwarming.
    ISO: 'groot-gas', // spouwmuurisolatie
    ET27: 'groot-gas', // voorzetraam (ca. €230 per m²)
    WG10: 'groot-gas', // inductie i.p.v. gasfornuis: brandstofswitch, gas eraf
    // Groot elektra — apparaat vervangen.
    WG01: 'groot-elektra', // koelvriescombinatie vervangen
    // Overig staat er niet in: EG20 (energiedisplay, gas én stroom), CF01
    // (zonwering), VL01 (rookmelders), warme sokken, textiel, boom, en de
    // gedragscodes EG04/EG10/EG11 als ze ooit geen advies zijn.
}

/**
 * @param {{Title?: string, Kind?: string, InterventionCode?: string}|null|undefined} material - Een `Materials`-document
 * @returns {SisaMeasureCategory}
 */
const sisaMeasureCategory = (material) => {
    if (!material) return 'overig'
    if (isAdvice(material)) return 'advies'
    // Zelfde volgorde als resolveMaterialIntervention: eigen code, dan de standaard.
    const code = material.InterventionCode || defaultForMaterial(material.Title)?.code || ''
    return BY_CODE[code] || 'overig'
}

export { SISA_MEASURE_CATEGORIES }
export default sisaMeasureCategory
